import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import type { Api } from '../api'
import { ApiError } from '../api/types'
import type { StoredReaderView } from '../api/types'
import type { ReportSlug } from '../api/viewScope'
import { inputClass, useDropdownEdge } from './ui'

/** The menu's own width, so the edge decision measures the right box. */
const PANEL_WIDTH = 300

/**
 * SAVED VIEWS — a reader's own named screens.
 *
 * `gridLayoutStore` keeps a grid's columns where the reader left them, silently
 * and with no interface. This is the layer on top: a whole screen — columns,
 * filters, currency, the period, the tab's own pickers — arranged, named
 * "KSA overdue", and returned to. One view per tab can be the default, and
 * opening that tab applies it.
 *
 * WHAT THIS FILE DOES NOT KNOW: what is in a view. The payload is produced and
 * consumed by each page's own `capture()` and `apply()`, and everything here
 * treats it as a string. That is what keeps three genuinely different filter
 * shapes — Receivables' optional fields, Sales' optional fields, Material
 * Planning's page-local interface using '' for unset — out of both this
 * component and the database.
 *
 * FAILURE IS QUIET, always. Every one of these calls decorates a report that
 * works without it. A views menu that could not load must never become a panel
 * in front of the figures.
 */

/** The payload version this build writes and understands. */
export const VIEW_SCHEMA = 1

/**
 * What a page hands back from `capture()`.
 *
 * Deliberately loose: the shape is the page's business. `v` is the only field
 * this layer reads, and only to refuse a payload from a build that meant
 * something different by the same field names.
 */
export interface ViewPayload {
  v: number
  [key: string]: unknown
}

/** What `apply()` reports back: the parts of a view that no longer applied. */
export interface DropReport {
  /** Reader-facing names, already in the words the screen uses. */
  dropped: string[]
}

export const NOTHING_DROPPED: DropReport = { dropped: [] }

/**
 * Every view this reader has for one report, and the four things you can do
 * with them. Loaded once per report, not per tab.
 *
 * `identity` is whoever the session currently is — the person being acted as,
 * under Login As. It reloads when that changes, because the views belong to
 * them and not to the administrator looking through their eyes.
 */
export function useSavedViews(api: Api, report: ReportSlug, identity: string | null) {
  const [views, setViews] = useState<StoredReaderView[]>([])
  const [loaded, setLoaded] = useState(false)

  const reload = useCallback(async () => {
    try {
      setViews(await api.readerViews(report))
    } catch {
      // No views is how the report behaved before this existed.
      setViews([])
    } finally {
      setLoaded(true)
    }
  }, [api, report])

  useEffect(() => {
    setLoaded(false)
    void reload()
  }, [reload, identity])

  const save = useCallback(
    async (page: string, name: string, payload: ViewPayload, makeDefault: boolean) => {
      /* The id comes back so the caller can mark the view ACTIVE. The screen
         you just saved is the screen you are on, and a menu that went on
         reading "Views" straight after would be the one place the feature
         forgot what had happened. */
      const { viewId } = await api.saveReaderView({
        report, page, name, view: JSON.stringify(payload), makeDefault,
      })
      await reload()
      return viewId
    },
    [api, report, reload],
  )

  const remove = useCallback(
    async (viewId: number) => {
      await api.deleteReaderView(viewId)
      await reload()
    },
    [api, reload],
  )

  /* The name comes back to the caller so a failed rename can say why in the
     row it happened in. Everything else here swallows failure, because it
     decorates a report that works without it -- a rename does not: the reader
     typed something and is owed an answer. */
  const rename = useCallback(
    async (viewId: number, name: string) => {
      await api.renameReaderView(viewId, name)
      await reload()
    },
    [api, reload],
  )

  const setDefault = useCallback(
    async (viewId: number, isDefault: boolean) => {
      await api.setDefaultReaderView(viewId, isDefault)
      await reload()
    },
    [api, reload],
  )

  return { views, loaded, reload, save, remove, rename, setDefault }
}

/**
 * Parse a stored payload, defensively.
 *
 * It came from a database and could have been written by an older build, a
 * newer one, or a bug. Anything that is not the shape it claims returns null
 * and the view is skipped — never an exception, which would take a report down
 * over a saved preference.
 */
export function parseView(json: string, schemaVersion: number): ViewPayload | null {
  if (schemaVersion !== VIEW_SCHEMA) return null
  try {
    const raw = JSON.parse(json) as unknown
    if (!raw || typeof raw !== 'object') return null
    const p = raw as ViewPayload
    return p.v === VIEW_SCHEMA ? p : null
  } catch {
    return null
  }
}

/**
 * IS THE SCREEN STILL THE VIEW IT SAYS IT IS?
 *
 * Needed the moment the view's NAME moved next to the page title. As a button
 * in the corner it was a control that happened to be labelled; beside the
 * heading it is a caption, and a caption has to be true. Change one filter
 * after applying "KSA overdue" and the screen is no longer KSA overdue -- so
 * the chip says so, and the menu offers to update it.
 *
 * COMPARED THROUGH A KEY-SORTED SERIALISATION, not JSON.stringify, and that is
 * not fastidiousness. A filter object is rebuilt by spreading -- `{...value,
 * [field]: v}` -- so clearing a filter and setting it again leaves the same
 * filters in a different key order. A plain stringify would call that an edit,
 * and a screen that claims to be edited when nothing changed teaches the reader
 * to ignore the one place it matters.
 */
function stableJson(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null'
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`
  const o = value as Record<string, unknown>
  const parts: string[] = []
  for (const k of Object.keys(o).sort()) {
    // Dropped exactly as JSON.stringify drops them; `{bu: undefined}` is what
    // a filter bar writes when a picker is cleared, and it is not a value.
    if (o[k] === undefined) continue
    parts.push(`${JSON.stringify(k)}:${stableJson(o[k])}`)
  }
  return `{${parts.join(',')}}`
}

export function useViewDirty(
  views: StoredReaderView[],
  activeId: number | null,
  capture: () => ViewPayload,
): boolean {
  return useMemo(() => {
    if (activeId === null) return false
    const v = views.find((x) => x.viewId === activeId)
    if (!v) return false
    const saved = parseView(v.view, v.schemaVersion)
    if (!saved) return false
    return stableJson(capture()) !== stableJson(saved)
  }, [views, activeId, capture])
}

/* ===========================================================================
 * THE THREE ROW ACTIONS — rename, default, delete.
 *
 * DRAWN, NOT TYPED. They were text glyphs until 24 Sep 2026: ★ ☆ ✕, each from
 * whatever font happened to have it, which is why they never matched. A star
 * and a multiplication sign are designed by different people for different
 * purposes; they differ in weight, in cap height and in how much of their own
 * em they fill, so no amount of padding makes three of them line up. Adding a
 * pencil to that set would have been a fourth typeface in a 90px strip.
 *
 * So they are inline SVG on one grid: a 24-unit box, 1.75 stroke, round caps
 * and joins, sized to 16px inside a 28px square target. They now share a
 * weight, an optical centre and a hit area — which is what "the same size"
 * actually requires.
 *
 * ONE TONE CARRIES MEANING, THE OTHER TWO DO NOT. All three sit at ink-4 and
 * come up to ink on hover, EXCEPT the star when it is on: that is the only one
 * of the three showing STATE rather than offering an act, so it stays in the
 * brand colour whether or not anything is hovered. Delete turns red on
 * approach, because the cost of the two neighbours is a keystroke and the cost
 * of this one is the view.
 * ======================================================================== */

type IconProps = { filled?: boolean }

/** A 24-unit box, so the three share a grid. */
function Icon({ children, filled = false }: { children: ReactNode; filled?: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden
      className="h-4 w-4"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </svg>
  )
}

function PencilIcon() {
  return (
    <Icon>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7.5 18.5 3.5 19.5l1-4Z" />
    </Icon>
  )
}

function StarIcon({ filled }: IconProps) {
  return (
    <Icon filled={filled}>
      <path d="m12 3.6 2.6 5.3 5.8.9-4.2 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.6 9.8l5.8-.9Z" />
    </Icon>
  )
}

function TrashIcon() {
  return (
    <Icon>
      <path d="M4 6.5h16" />
      <path d="M9.5 6.5V4.8A1.3 1.3 0 0 1 10.8 3.5h2.4a1.3 1.3 0 0 1 1.3 1.3v1.7" />
      <path d="M6.5 6.5 7.4 19a1.5 1.5 0 0 0 1.5 1.4h6.2a1.5 1.5 0 0 0 1.5-1.4l.9-12.5" />
    </Icon>
  )
}

/**
 * One of the three. A 28px square whatever is inside it, so the strip reads as
 * a row of equals rather than as three different controls that happen to be
 * adjacent.
 */
function RowAction({
  label,
  title,
  onClick,
  icon: IconComponent,
  tone = 'quiet',
  pressed,
}: {
  /** For anybody not looking at the icon. */
  label: string
  /** For anybody looking at it and unsure. */
  title: string
  onClick: () => void
  icon: (props: IconProps) => ReactNode
  tone?: 'quiet' | 'on' | 'danger'
  pressed?: boolean
}) {
  const colour =
    tone === 'on'
      ? 'text-(--color-accent) hover:bg-(--color-accent)/10'
      : tone === 'danger'
        ? 'text-(--color-ink-4) group-hover:text-(--color-ink-3) hover:!text-(--color-overdue) hover:bg-(--color-overdue)/10'
        : 'text-(--color-ink-4) group-hover:text-(--color-ink-3) hover:!text-(--color-ink) hover:bg-(--color-surface-3)'
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={label}
      {...(pressed === undefined ? {} : { 'aria-pressed': pressed })}
      className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors ${colour}`}
    >
      <IconComponent filled={tone === 'on'} />
    </button>
  )
}
/**
 * The view control, for one tab.
 *
 * ON THE TITLE LINE, not in the header's right slot -- moved there 24 Sep 2026
 * because the first placement was wrong in a way worth recording. This control
 * is half a label and half a control: with a view applied it shows that view's
 * NAME, which is a statement about what you are looking at rather than a word
 * describing what a button does. Sat beside the currency toggle, which is
 * purely a control, it read as a stray button and nobody found it -- the owner
 * asked "where can i save a view" with it on screen.
 *
 * Beside the heading the two read as one phrase: Aging Dashboard, test view.
 * It costs no height, which on this platform is the scarce axis, because the
 * title line has width to spare and the right slot did not.
 *
 * NOT in the grid's chrome, which was the other candidate: a view is per TAB
 * and GridChrome is provided per CARD, so it would be absent on exactly the
 * tabs whose filters are most worth saving -- Receivables' Tree View and
 * Trends have no grid at all.
 *
 * A PILL, NOT A BUTTON, and the shape is the argument. Everything else in this
 * chrome is a rectangle because everything else is a control; this is rounded
 * because it is primarily a label that can be opened.
 */
export function SavedViewsMenu({
  views,
  activeId,
  acting,
  dirty = false,
  onApply,
  onRename,
  onSave,
  onDelete,
  onSetDefault,
}: {
  /** Already narrowed to this tab by the caller. */
  views: StoredReaderView[]
  activeId: number | null
  /** Under Login As these are somebody else's views; saving says whose. */
  acting: string | null
  /** The screen has moved since the active view was applied. */
  dirty?: boolean
  onApply: (view: StoredReaderView) => void
  /** Rejects on a name this reader already uses here; the row says so. */
  onRename: (viewId: number, name: string) => Promise<unknown>
  onSave: (name: string, makeDefault: boolean) => Promise<unknown>
  onDelete: (viewId: number) => void
  onSetDefault: (viewId: number, isDefault: boolean) => void
}) {
  const [open, setOpen] = useState(false)
  const [naming, setNaming] = useState(false)
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  /* The view whose deletion is being confirmed, if any. Held here rather
     than per row so only one question can be open at a time. */
  const [confirming, setConfirming] = useState<number | null>(null)
  /* The view being renamed, the text so far, and the one refusal a rename can
     come back with -- a name this reader already uses on this tab. Held here
     rather than per row so only one question is ever open. */
  const [renaming, setRenaming] = useState<number | null>(null)
  const [renameText, setRenameText] = useState('')
  const [renameError, setRenameError] = useState<string | null>(null)
  const renameRef = useRef<HTMLInputElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  /* The panel hangs from whichever edge keeps it on the glass. On the title
     line that is almost always the left, but the header wraps on a narrow
     screen and this control moves with it. See useDropdownEdge. */
  const { fromRight, style: edgeStyle } = useDropdownEdge(open, PANEL_WIDTH, rootRef)

  useEffect(() => {
    if (!open) return
    const away = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false)
        setNaming(false)
        setConfirming(null)
        setRenaming(null)
      }
    }
    document.addEventListener('mousedown', away)
    return () => document.removeEventListener('mousedown', away)
  }, [open])

  useEffect(() => {
    if (naming) inputRef.current?.focus()
  }, [naming])

  /* SELECTED, not just focused. The box opens holding the current name, and
     renaming usually means replacing it rather than editing a character of it,
     so the first keystroke should do that. */
  useEffect(() => {
    if (renaming === null) return
    renameRef.current?.focus()
    renameRef.current?.select()
  }, [renaming])

  const active = useMemo(() => views.find((v) => v.viewId === activeId) ?? null, [views, activeId])
  const empty = views.length === 0

  const commit = async (overrideName?: string) => {
    const trimmed = (overrideName ?? name).trim()
    if (!trimmed || busy) return
    setBusy(true)
    try {
      /* MAKE IT THE DEFAULT ONLY IF THERE IS NO OTHER. Somebody's first view on
         a tab is almost always the one they want to open on, and a separate
         trip to a menu to say so is friction for the common case. A second view
         does NOT quietly take the title from the first. */
      await onSave(trimmed, views.length === 0)
      setName('')
      setNaming(false)
      setOpen(false)
    } finally {
      setBusy(false)
    }
  }

  const openRename = (v: StoredReaderView) => {
    setConfirming(null)
    setRenameError(null)
    setRenameText(v.name)
    setRenaming(v.viewId)
  }

  const closeRename = () => {
    setRenaming(null)
    setRenameError(null)
  }

  const commitRename = async (v: StoredReaderView) => {
    const trimmed = renameText.trim()
    if (!trimmed || busy) return
    // Pressing Rename on an unchanged name is somebody closing the box.
    if (trimmed === v.name) {
      closeRename()
      return
    }
    setBusy(true)
    try {
      await onRename(v.viewId, trimmed)
      closeRename()
    } catch (e) {
      /* THE ONE PLACE THIS COMPONENT DOES NOT SWALLOW A FAILURE. Everything
         else here decorates a report that works without it; a rename does not,
         because the reader typed something and is owed an answer -- and the
         answer they will usually get is that the name is already theirs. */
      /* ONLY THE REFUSAL WE DESIGNED GETS TO SPEAK FOR ITSELF. Printing any
         error's title here put ASP.NET's "An error occurred while processing
         your request." under the box as though it were advice about the name --
         seen on 24 Sep 2026, when the procedure was in the repository and not
         yet in the database. A clash is the reader's problem and is worth their
         words; everything else is ours. */
      const taken =
        (e instanceof ApiError && e.failure.kind === 'error' && e.failure.outcome === 'DENIED_NAME_TAKEN') ||
        (e instanceof Error && e.message === 'DENIED_NAME_TAKEN')
      setRenameError(
        taken
          ? `You already have a view called “${trimmed}” on this screen.`
          : 'That name could not be saved. The view is unchanged.',
      )
    } finally {
      setBusy(false)
    }
  }

  /* WITH NOTHING SAVED THERE IS ONLY ONE THING TO DO, so the first click does
     it rather than opening a menu whose only row is "save". */
  const toggle = () => {
    const next = !open
    setOpen(next)
    setConfirming(null)
    setRenaming(null)
    if (next && empty) {
      setName('')
      setNaming(true)
    } else if (!next) {
      setNaming(false)
    }
  }

  const chip = active
    ? /* APPLIED. The brand wash and the weight say this screen has a name. */
      'border-(--color-accent)/45 bg-(--color-accent)/8 text-(--color-ink) hover:bg-(--color-accent)/14'
    : empty
      ? /* NOTHING SAVED YET. A dashed outline is the house sign for a slot
           waiting to be filled, and it keeps a first-run report calm. */
        'border-dashed border-(--color-line-strong) text-(--color-ink-3) hover:border-(--color-accent) hover:text-(--color-accent)'
      : 'border-(--color-line-strong) text-(--color-ink-2) hover:bg-(--color-surface-2) hover:text-(--color-ink)'

  return (
    <div ref={rootRef} className="relative no-print inline-flex">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="menu"
        title={
          active
            ? dirty
              ? `Showing "${active.name}", changed since you applied it`
              : `Showing "${active.name}"`
            : empty
              ? 'Save this screen as a view you can come back to'
              : 'Your saved views for this screen'
        }
        className={`inline-flex max-w-[22rem] items-center gap-1.5 rounded-full border px-3 py-1 text-(length:--text-small) transition-colors ${
          acting
            ? 'border-(--color-privileged)/50 bg-(--color-privileged)/8 text-(--color-privileged) hover:bg-(--color-privileged)/14'
            : chip
        }`}
      >
        {/* The star means the same thing it means in the list below: this is
            the view that opens by itself. Absent on the others, so it is a
            fact about the view rather than decoration on the control. */}
        {active?.isDefault ? <span aria-hidden>★</span> : null}
        <span className="truncate font-semibold">
          {active ? active.name : empty ? 'Save this view' : 'Saved views'}
        </span>
        {/* EDITED, SAID PLAINLY. The name beside a page title is a caption, and
            a caption that keeps its word after the reader has changed a filter
            is the screen quietly lying about itself. */}
        {active && dirty ? (
          <span className="shrink-0 font-normal text-(--color-ink-3)">· Edited</span>
        ) : null}
        {!active && !empty ? (
          <span className="shrink-0 font-normal text-(--color-ink-3)">{views.length}</span>
        ) : null}
        <span aria-hidden className="shrink-0 text-(--color-ink-4)">
          {empty ? '+' : '▾'}
        </span>
      </button>

      {open && (
        /* z-40, above the grids. A popover and a grid's sticky header both sat
           at z-30 until 24 Sep 2026 and the table won on DOM order. */
        <div
          role="menu"
          className={`absolute top-[calc(100%+6px)] z-40 w-[300px] overflow-hidden rounded-md border border-(--color-line-strong) bg-(--color-surface) shadow-lg ${
            fromRight ? 'right-0' : 'left-0'
          }`}
          /* Only ever set on a phone, where neither edge of the pill leaves
             room for 300px; see useDropdownEdge. */
          style={edgeStyle}
        >
          {empty ? null : (
            <ul className="max-h-64 overflow-y-auto scroll-slim py-1">
              {views.map((v) => (
                <li key={v.viewId} className="px-1">
                  {renaming === v.viewId ? (
                    /* RENAMING HAPPENS IN THE ROW, like the delete question and
                       for the same reason: the list around it is the context
                       that makes the new name a good or a bad one. A dialog
                       would cover the names you are trying not to collide
                       with -- and a collision is the one refusal this can get. */
                    <form
                      className="flex flex-col gap-1.5 px-1 py-1.5"
                      onSubmit={(e) => {
                        e.preventDefault()
                        void commitRename(v)
                      }}
                    >
                      <input
                        ref={renameRef}
                        value={renameText}
                        maxLength={60}
                        onChange={(e) => {
                          setRenameText(e.target.value)
                          setRenameError(null)
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') closeRename()
                        }}
                        aria-label={`Rename ${v.name}`}
                        aria-invalid={renameError !== null}
                        className={inputClass}
                      />
                      {renameError ? (
                        <p className="text-(length:--text-small) text-(--color-overdue)">{renameError}</p>
                      ) : null}
                      <div className="flex gap-2">
                        <button
                          type="submit"
                          disabled={busy || renameText.trim().length === 0}
                          className="rounded-md bg-(--color-navy-700) px-3 py-1 text-(length:--text-small) font-semibold text-white disabled:opacity-40"
                        >
                          {busy ? 'Renaming…' : 'Rename'}
                        </button>
                        <button
                          type="button"
                          onClick={closeRename}
                          className="rounded-md border border-(--color-line-strong) px-3 py-1 text-(length:--text-small) font-semibold text-(--color-ink-2)"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  ) : confirming === v.viewId ? (
                    /* DELETING IS THE ONLY IRREVERSIBLE THING THIS MENU DOES.
                       There is no undo and the server keeps no copy, and the
                       bin sits a few pixels from the star and from the name. So
                       the ROW becomes the question instead of a dialog opening
                       over it: the name stays on screen beside the answer,
                       which is exactly what a modal would have covered up. */
                    <div className="flex min-w-0 items-center gap-1 py-1 pl-2">
                      <span className="min-w-0 flex-1 truncate text-(length:--text-small) text-(--color-ink-2)">
                        Delete “{v.name}”?
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          onDelete(v.viewId)
                          setConfirming(null)
                        }}
                        aria-label={`Confirm deleting ${v.name}`}
                        className="shrink-0 rounded px-2 py-1 text-(length:--text-small) font-semibold text-(--color-overdue) hover:bg-(--color-overdue)/10"
                      >
                        Delete
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirming(null)}
                        className="shrink-0 rounded px-2 py-1 text-(length:--text-small) font-semibold text-(--color-ink-3) hover:bg-(--color-surface-3)"
                      >
                        Keep
                      </button>
                    </div>
                  ) : (
                    /* THE ROW IS A GROUP, not a button with things after it.
                       `group` lets the three actions stay quiet until the row is
                       under the cursor without ever being hidden -- they keep
                       their space, so nothing reflows, and they are reachable by
                       keyboard whether or not a mouse ever arrives. */
                    <div className="group flex items-center gap-0.5 rounded-md pr-0.5 hover:bg-(--color-surface-2)">
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          onApply(v)
                          setOpen(false)
                        }}
                        className={`min-w-0 flex-1 truncate rounded-md px-2 py-1.5 text-left text-(length:--text-body) ${
                          v.viewId === activeId ? 'font-semibold text-(--color-ink)' : 'text-(--color-ink-2)'
                        }`}
                        title={v.name}
                      >
                        {v.name}
                      </button>
                      <RowAction
                        label={`Rename ${v.name}`}
                        title="Rename"
                        onClick={() => openRename(v)}
                        icon={PencilIcon}
                      />
                      {/* THE DEFAULT IS A TOGGLE, not a separate screen.
                          Pressing it on the current default clears it, which is
                          the only way to say "open this tab as it ships" once
                          you have one. It is the one action that shows STATE as
                          well as offering an act, so it alone stays coloured
                          when the row is not hovered. */}
                      <RowAction
                        label={v.isDefault ? `Stop opening ${v.name} by default` : `Open ${v.name} by default`}
                        title={v.isDefault ? 'Opens by default' : 'Open by default'}
                        pressed={v.isDefault}
                        onClick={() => onSetDefault(v.viewId, !v.isDefault)}
                        icon={StarIcon}
                        tone={v.isDefault ? 'on' : 'quiet'}
                      />
                      <RowAction
                        label={`Delete ${v.name}`}
                        title="Delete"
                        onClick={() => {
                          setConfirming(v.viewId)
                          setRenaming(null)
                        }}
                        icon={TrashIcon}
                        tone="danger"
                      />
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className={empty ? 'p-2' : 'border-t border-(--color-line) p-2'}>
            {naming ? (
              <div className="flex flex-col gap-2">
                <input
                  ref={inputRef}
                  value={name}
                  maxLength={60}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void commit()
                    if (e.key === 'Escape') {
                      setNaming(false)
                      if (empty) setOpen(false)
                    }
                  }}
                  placeholder="Name this view…"
                  aria-label="Name this view"
                  className={inputClass}
                />
                {/* WHOSE ACCOUNT IT LANDS IN, said before it happens. Login As
                    substitutes the identity completely, so a save while acting
                    writes to THEIR account -- allowed by decision, and not
                    something anybody should do by accident. */}
                {acting && (
                  <p className="text-(length:--text-small) text-(--color-privileged)">
                    This will save to {acting}’s account.
                  </p>
                )}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => void commit()}
                    disabled={busy || name.trim().length === 0}
                    className="rounded-md bg-(--color-navy-700) px-3 py-1.5 text-(length:--text-small) font-semibold text-white disabled:opacity-40"
                  >
                    {busy ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setNaming(false)
                      if (empty) setOpen(false)
                    }}
                    className="rounded-md border border-(--color-line-strong) px-3 py-1.5 text-(length:--text-small) font-semibold text-(--color-ink-2)"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col">
                {/* UPDATING AN EDITED VIEW IS THE FIRST THING OFFERED, because
                    once the chip says "Edited" it is the thing the reader came
                    here to do. It reuses the save path: 037 replaces by name,
                    so this is the same call with the name already known. */}
                {active && dirty ? (
                  <button
                    type="button"
                    onClick={() => void commit(active.name)}
                    disabled={busy}
                    className="w-full rounded px-2 py-1.5 text-left text-(length:--text-small) font-semibold text-(--color-navy-700) hover:bg-(--color-surface-3) disabled:opacity-40"
                  >
                    {busy ? 'Saving…' : `Update “${active.name}” to this screen`}
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => {
                    setName(dirty ? '' : (active?.name ?? ''))
                    setNaming(true)
                  }}
                  className="w-full rounded px-2 py-1.5 text-left text-(length:--text-small) font-semibold text-(--color-navy-700) hover:bg-(--color-surface-3)"
                >
                  {active && dirty ? 'Save as a new view…' : 'Save this screen as a view…'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * The line that says a view did not fully apply.
 *
 * NOT A TOAST -- there is no toast system here, and a transient message about
 * whether the figures answer the question you asked is the wrong medium. One
 * line, under the header, dismissed by the next tab change or the next apply.
 *
 * "NOT AVAILABLE TO YOU HERE", never "no longer exists". At the client a column
 * missing because of column governance is indistinguishable from one that was
 * deleted, and telling somebody a column is gone when it is merely not theirs
 * would be a confident lie.
 */
export function ViewAdjustedNotice({ report, onDismiss }: { report: DropReport; onDismiss: () => void }) {
  if (report.dropped.length === 0) return null
  return (
    <p className="no-print shrink-0 text-(length:--text-small) text-(--color-ink-3)">
      This view was adjusted: {report.dropped.join(', ')}{' '}
      {report.dropped.length === 1 ? 'is' : 'are'} not available to you here, so{' '}
      {report.dropped.length === 1 ? 'it was' : 'they were'} left out. Everything else applied.{' '}
      <button
        type="button"
        onClick={onDismiss}
        className="font-semibold text-(--color-navy-700) underline underline-offset-2"
      >
        Dismiss
      </button>
    </p>
  )
}
